import { type DragEvent, useEffect, useRef, useState } from "react";
import { type CasoDetalle, type EvidenciaOut } from "@/lib/api";
import { useAuth, canEdit } from "@/lib/auth";
import ConfirmDialog from "@/ui/ConfirmDialog";
import AyudaVista from "@/ui/AyudaVista";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";
import GenerarPaqueteModal from "@/components/GenerarPaqueteModal";
import VisorArchivo from "@/ui/VisorArchivo";
import { subirEvidencia } from "@/components/evidencias/subirEvidencia";
import { enCursoCompresion, sinArchivoCompresion } from "@/components/evidencias/EstadoCompresion";
import { EvidenciaCard, EvidenciaFila } from "@/components/evidencias/EvidenciaItem";
import PaquetesPanel from "@/components/evidencias/PaquetesPanel";
import { copiarTexto } from "@/lib/copiar";
import { ayudaEvidencias } from "@/components/ayuda/vistas/evidencias";

type Props = {
  caso: CasoDetalle;
  onChanged: () => void;
};


export default function TabEvidencias({ caso, onChanged }: Props) {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [evidencias, setEvidencias] = useState<EvidenciaOut[]>([]);
  // Lo que mandó el proveedor se lista aparte (ver la sección de abajo). El
  // tag lo pone `services/adjuntos_proveedor` al guardarlo.
  const docsProveedor = evidencias.filter((e) => e.tags.includes("proveedor-doc"));
  // Lo que llegó ADJUNTO A UN CORREO también va aparte. Joe, al ver el formato
  // de RMA revuelto con las fotos del equipo: «estas no son evidencias carnal…
  // así queda separada bonito de las evidencias del soporte y no queda mega
  // mezclado, luego se hace un desmadre».
  const delCorreo = evidencias.filter(
    (e) => !e.tags.includes("proveedor-doc") && e.tags.includes("correo"),
  );
  const propias = evidencias.filter(
    (e) => !e.tags.includes("proveedor-doc") && !e.tags.includes("correo"),
  );
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progreso, setProgreso] = useState<{ nombre: string; pct: number } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [paqueteOpen, setPaqueteOpen] = useState(false);
  // Recarga los paquetes ZIP tras generar uno nuevo: generar NO cambia el
  // número de evidencias, así que no basta con evidencias.length (antes el
  // panel no se enteraba y había que refrescar el navegador).
  const [paquetesKey, setPaquetesKey] = useState(0);
  const [confirmDel, setConfirmDel] = useState<EvidenciaOut | null>(null);
  // Evidencia abierta en el visor (solo ver/descargar).
  const [verEv, setVerEv] = useState<EvidenciaOut | null>(null);
  // IMEI al que se asignaran las proximas subidas (drop o file picker).
  // null = general del caso (sin IMEI). Despues siempre se puede reasignar
  // por evidencia con el select que cada card tiene.
  const [uploadImeiId, setUploadImeiId] = useState<number | null>(null);
  // Comprimir videos: manda solo en videos <=100MB (los >100MB se comprimen
  // siempre). Encendido por default; se recuerda por navegador.
  const [comprimirVideos, setComprimirVideos] = useState<boolean>(
    () => localStorage.getItem("arktickets_comprimir_videos") !== "0",
  );
  // Vista de archivos: lista compacta (default, densa — muchos archivos sin
  // scrollear) o cuadrícula con previews grandes. Se recuerda por navegador.
  const [vista, setVista] = useState<"lista" | "cuadricula">(() =>
    localStorage.getItem("arktickets_evidencias_vista") === "cuadricula"
      ? "cuadricula"
      : "lista",
  );
  function cambiarVista(v: "lista" | "cuadricula") {
    setVista(v);
    localStorage.setItem("arktickets_evidencias_vista", v);
  }

  async function refresh() {
    setLoading(true);
    try {
      const res = await fetch(`/api/casos/${caso.id}/evidencias`, { credentials: "include" });
      if (!res.ok) throw new Error("Error al cargar evidencias");
      setEvidencias(await res.json());
    } catch (err) {
      toast.error("No pude cargar evidencias", String(err));
    } finally {
      setLoading(false);
    }
  }

  // Recarga sin el "Cargando..." (para el poll de compresión).
  async function refreshSilencioso() {
    try {
      const res = await fetch(`/api/casos/${caso.id}/evidencias`, { credentials: "include" });
      if (res.ok) setEvidencias(await res.json());
    } catch {
      /* red intermitente; el siguiente tick reintenta */
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  // Poll mientras haya videos comprimiéndose: la tarjeta pasa de
  // "comprimiendo" a "listo" sola, sin que el usuario recargue.
  useEffect(() => {
    if (!evidencias.some(enCursoCompresion)) return;
    const t = setInterval(() => void refreshSilencioso(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidencias]);

  // El subidor vive en components/evidencias/subirEvidencia: lo comparte con la
  // zona de fotos de la bitácora. Copiar el pipeline de subida ya salió mal una
  // vez (api/correos_adjuntos duplicó el del backend y derivó).
  async function subirUno(file: File, imeiId: number | null): Promise<void> {
    await subirEvidencia({
      casoId: caso.id,
      file,
      imeiId,
      comprimir: comprimirVideos,
      onProgreso: setProgreso,
    });
  }

  async function uploadFiles(files: FileList | File[], imeiId: number | null = null) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const file of list) {
      try {
        await subirUno(file, imeiId);
        ok++;
      } catch (err) {
        fail++;
        const msg = err instanceof Error ? err.message : "Error";
        toast.error(`Falló ${file.name}`, msg);
      }
    }
    setProgreso(null);
    setUploading(false);
    if (ok > 0) toast.success(`${ok} archivo${ok === 1 ? "" : "s"} subido${ok === 1 ? "" : "s"}`);
    await refresh();
    onChanged();
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    if (!canEdit(me)) {
      toast.error("Sin permiso para subir");
      return;
    }
    if (e.dataTransfer.files?.length) {
      void uploadFiles(e.dataTransfer.files, uploadImeiId);
    }
  }

  async function eliminar(ev: EvidenciaOut) {
    try {
      const res = await fetch(`/api/evidencias/${ev.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      toast.success("Evidencia eliminada", ev.nombre_original);
      await refresh();
      onChanged();
    } catch (err) {
      toast.error("No se pudo eliminar", String(err));
    }
  }

  // «Descartar» un video que falló al comprimirse borra también el original
  // que se guardaba para reintentar: se confirma. El «Descartar aviso» del que
  // quedó muy pesado no pierde nada (el archivo ya no se guardó) y va directo.
  function descartar(ev: EvidenciaOut) {
    if (ev.estado_compresion === "error") setConfirmDel(ev);
    else void eliminar(ev);
  }

  async function reintentar(ev: EvidenciaOut) {
    try {
      const res = await fetch(`/api/evidencias/${ev.id}/reintentar`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      toast.success("Reintentando compresión");
      await refreshSilencioso();
    } catch (err) {
      toast.error("No se pudo reintentar", String(err));
    }
  }

  async function compartirEnlace(ev: EvidenciaOut) {
    try {
      const res = await fetch(`/api/evidencias/${ev.id}/enlace`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      const { url } = (await res.json()) as { url: string };
      if (!(await copiarTexto(url))) throw new Error("No pude copiar el enlace — cópialo a mano: " + url);
      toast.success("Enlace copiado", "Pégalo en el ticket externo (público, 90 días)");
    } catch (err) {
      toast.error("No se pudo generar el enlace", String(err));
    }
  }

  async function asignarImei(ev: EvidenciaOut, imeiId: number | null) {
    try {
      const res = await fetch(`/api/evidencias/${ev.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ imei_id: imeiId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      await refresh();
    } catch (err) {
      toast.error("No se pudo actualizar", String(err));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-medium text-zinc-300">
            Evidencias{" "}
            <span className="text-zinc-500">({evidencias.length})</span>
          </h2>
          <AyudaVista titulo="Evidencias" {...ayudaEvidencias({ puedeEditar: canEdit(me) })} />
          <div className="flex rounded-md border border-ink-600 overflow-hidden text-[11px]">
            <button
              onClick={() => cambiarVista("lista")}
              className={`px-2 py-0.5 ${
                vista === "lista"
                  ? "bg-ink-700 text-zinc-200"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              ☰ Lista
            </button>
            <button
              onClick={() => cambiarVista("cuadricula")}
              className={`px-2 py-0.5 border-l border-ink-600 ${
                vista === "cuadricula"
                  ? "bg-ink-700 text-zinc-200"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              ▦ Cuadrícula
            </button>
          </div>
        </div>
        {canEdit(me) && (
          <button
            onClick={() => setPaqueteOpen(true)}
            disabled={evidencias.length === 0}
            className="btn-primary disabled:opacity-50 w-full sm:w-auto"
          >
            + Generar paquete ZIP
          </button>
        )}
      </div>

      {/* Dropzone con selector de IMEI + toggle de compresión */}
      {canEdit(me) && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-center sm:justify-between">
            {caso.imeis.length > 0 ? (
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                {/* nowrap: en anchos medios (~1000-1200px) la etiqueta se partía en dos
                    renglones y la ayudita también, y se veían encimados. */}
                <span className="text-zinc-400 uppercase tracking-wide whitespace-nowrap shrink-0">
                  Próximas subidas →
                </span>
                <select
                  className="input text-xs py-1 w-full sm:max-w-[280px]"
                  value={uploadImeiId ?? ""}
                  onChange={(e) =>
                    setUploadImeiId(e.target.value ? parseInt(e.target.value) : null)
                  }
                >
                  <option value="">General del caso (sin IMEI)</option>
                  {caso.imeis.map((i) => (
                    <option key={i.id} value={i.id}>
                      IMEI {i.imei}
                      {i.modelo_real ? ` · ${i.modelo_real}` : ""}
                    </option>
                  ))}
                </select>
                <span className="text-zinc-500 hidden xl:inline whitespace-nowrap">
                  · luego puedes reasignar cada archivo
                </span>
              </div>
            ) : (
              <span />
            )}
            {/* Interruptor (no checkbox): es un on/off, igual que el resto de la app. */}
            <Toggle
              checked={comprimirVideos}
              onChange={(v) => {
                setComprimirVideos(v);
                localStorage.setItem("arktickets_comprimir_videos", v ? "1" : "0");
              }}
              label="Comprimir videos"
              hint="Los de más de 100 MB se comprimen siempre; esto decide los más chicos."
              className="w-fit"
            />
          </div>
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`card border-dashed cursor-pointer text-center transition ${
              dragActive ? "border-accent-500 bg-accent-500/5" : "border-ink-600"
            } ${uploading ? "opacity-80 pointer-events-none" : ""}`}
          >
            <p className="text-sm text-zinc-400">
              {uploading
                ? progreso
                  ? `Subiendo ${progreso.nombre} — ${progreso.pct}%`
                  : "Subiendo…"
                : dragActive
                ? "Suelta los archivos aquí"
                : "Arrastra archivos aquí, o click para seleccionar"}
            </p>
            {uploading && progreso && (
              <div className="mt-2 h-1.5 rounded-full bg-ink-700 overflow-hidden">
                <div
                  className="h-full bg-accent-500 transition-all"
                  style={{ width: `${progreso.pct}%` }}
                />
              </div>
            )}
            <p className="text-xs text-zinc-600 mt-1">
              Video: se comprime automáticamente si pasa de 100 MB (el tope de 200 MB
              aplica al archivo ya comprimido). Otros archivos: máx. 200 MB. Se
              conservan 18 meses.{" "}
              {uploadImeiId
                ? `Se asignarán a IMEI ${
                    caso.imeis.find((i) => i.id === uploadImeiId)?.imei
                  }.`
                : "Sin IMEI por defecto — reasigna por archivo después si quieres."}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void uploadFiles(e.target.files, uploadImeiId);
                e.target.value = "";
              }}
            />
          </div>
        </div>
      )}

      {/* Paquetes ZIP arriba: contraseña + enlaces son lo que se comparte
          al cliente — no deben quedar sepultados bajo la lista de archivos. */}
      <PaquetesPanel caso={caso} refreshKey={paquetesKey} ticketExterno={caso.ticket_externo} />

      {/* Archivos (lista compacta o cuadrícula) */}
      <div className="card">
        {loading ? (
          <p className="text-sm text-zinc-500">Cargando...</p>
        ) : propias.length === 0 ? (
          <p className="text-sm text-zinc-500">
            {docsProveedor.length > 0
              ? "Sin evidencias nuestras todavía — abajo están los documentos que mandó el proveedor."
              : "Sin evidencias todavía."}
          </p>
        ) : (
          vista === "cuadricula" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {propias.map((e) => (
              <EvidenciaCard
                key={e.id}
                ev={e}
                imeis={caso.imeis}
                puedeEditar={canEdit(me)}
                onAsignar={(im) => asignarImei(e, im)}
                onEliminar={() => setConfirmDel(e)}
                onDescartar={() => descartar(e)}
                onReintentar={() => reintentar(e)}
                onCompartir={() => compartirEnlace(e)}
                onVer={() => setVerEv(e)}
              />
            ))}
          </div>
          ) : (
          <ul className="divide-y divide-ink-800">
            {propias.map((e) => (
              <EvidenciaFila
                key={e.id}
                ev={e}
                imeis={caso.imeis}
                puedeEditar={canEdit(me)}
                onAsignar={(im) => asignarImei(e, im)}
                onEliminar={() => setConfirmDel(e)}
                onDescartar={() => descartar(e)}
                onReintentar={() => reintentar(e)}
                onCompartir={() => compartirEnlace(e)}
                onVer={() => setVerEv(e)}
              />
            ))}
          </ul>
          )
        )}
      </div>

      {/* Lo que llegó por CORREO va aparte, por lo mismo que lo del proveedor:
          el formato de RMA que mandó el cliente y la foto del equipo roto son
          cosas distintas y se buscan en momentos distintos. */}
      {delCorreo.length > 0 && (
        <div className="card">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold">
              ✉️ Archivos que llegaron por correo{" "}
              <span className="font-normal text-zinc-500">({delCorreo.length})</span>
            </h3>
            <p className="text-[11px] text-zinc-500">
              Venían adjuntos —o detrás de un enlace— en un correo del caso. Las imágenes del
              cuerpo (firmas, capturas pegadas) no están aquí: se ven dentro del correo.
            </p>
          </div>
          <ul className="divide-y divide-ink-800">
            {delCorreo.map((e) => (
              <EvidenciaFila
                key={e.id}
                ev={e}
                imeis={caso.imeis}
                puedeEditar={canEdit(me)}
                onAsignar={(im) => asignarImei(e, im)}
                onEliminar={() => setConfirmDel(e)}
                onDescartar={() => descartar(e)}
                onReintentar={() => reintentar(e)}
                onCompartir={() => compartirEnlace(e)}
                onVer={() => setVerEv(e)}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Lo que mandó el PROVEEDOR va aparte, no revuelto con nuestras
          evidencias: son cosas distintas (sus manuales y logs contra nuestras
          fotos del equipo) y se buscan en momentos distintos. Se guardan solas
          al revisar el ticket; antes solo quedaba la liga en la bitácora, que
          muere el día que el proveedor rote su almacenamiento. */}
      {docsProveedor.length > 0 && (
        <div className="card">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <h3 className="text-sm font-semibold">
              📥 Documentos del proveedor{" "}
              <span className="text-zinc-500 font-normal">({docsProveedor.length})</span>
            </h3>
            <p className="text-[11px] text-zinc-500">
              Llegaron en su ticket y se guardaron aquí. No se preseleccionan al armar el ZIP.
            </p>
          </div>
          <ul className="divide-y divide-ink-800">
            {docsProveedor.map((e) => (
              <EvidenciaFila
                key={e.id}
                ev={e}
                imeis={caso.imeis}
                puedeEditar={canEdit(me)}
                onAsignar={(im) => asignarImei(e, im)}
                onEliminar={() => setConfirmDel(e)}
                onDescartar={() => descartar(e)}
                onReintentar={() => reintentar(e)}
                onCompartir={() => compartirEnlace(e)}
                onVer={() => setVerEv(e)}
              />
            ))}
          </ul>
        </div>
      )}

      {paqueteOpen && (
        <GenerarPaqueteModal
          caso={caso}
          evidencias={evidencias}
          onClose={() => setPaqueteOpen(false)}
          onCreated={() => {
            setPaquetesKey((k) => k + 1);
            onChanged();
          }}
        />
      )}

      {verEv && (() => {
        // Carrusel: todas las evidencias VISIBLES (purgadas o comprimiéndose no
        // tienen archivo servible).
        const visibles = evidencias.filter(
          (x) => x.archivo_eliminado_at === null && !enCursoCompresion(x) && !sinArchivoCompresion(x),
        );
        return (
          <VisorArchivo
            targets={visibles.map((x) => ({
              nombre: x.nombre_original,
              mime: x.mime,
              url: `/api/evidencias/${x.id}/file`,
              previewUrl: `/api/evidencias/${x.id}/preview`,
            }))}
            inicial={Math.max(0, visibles.findIndex((x) => x.id === verEv.id))}
            onClose={() => setVerEv(null)}
          />
        );
      })()}

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={
            confirmDel.estado_compresion === "error"
              ? `Descartar "${confirmDel.nombre_original}"`
              : `Eliminar "${confirmDel.nombre_original}"`
          }
          description={
            confirmDel.estado_compresion === "error"
              ? "El video no se pudo comprimir, pero el original sigue guardado para «Reintentar». Descartarlo borra también el original: si lo vuelves a necesitar, hay que subirlo otra vez."
              : "Se borra del disco y de la base de datos. Los paquetes generados que la contienen siguen funcionando."
          }
          variant="danger"
          confirmLabel={confirmDel.estado_compresion === "error" ? "Descartar" : "Eliminar"}
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </div>
  );
}
